import { FC, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useQuoteModals } from 'features/Work/pages/Quotes/hooks/useQuoteModals';
import * as routes from 'features/Work/routes';

import { MenuItem } from '../../../../../../components/EditMenu/types';
import WorkListItem from '../../../../../../components/WorkListItem';
import { QuoteFromList } from '../../../../../hooks/useGetQuotes';
import locales from './locales';

interface QuoteListItemProps {
  quote: QuoteFromList;
}

const QuoteListItem: FC<QuoteListItemProps> = ({ quote }) => {
  const history = useHistory();
  const { openModal } = useQuoteModals();

  const menuItemsByStatus = useMemo(() => {
    const { generatedInvoiceId, status } = quote;
    const wasAlreadyConvertedToInvoice = !!generatedInvoiceId;

    const quoteSeeConvertedInvoiceItem: MenuItem = {
      icon: 'invoice',
      onClick: () => history.push(routes.getInvoice(generatedInvoiceId ?? '')),
      title: locales.seeInvoice,
    };

    const quoteTurnIntoInvoiceItem: MenuItem = {
      icon: 'invoice',
      onClick: () =>
        openModal({
          name: 'convert-quote-to-invoice',
          props: {
            ...quote,
            isFromList: true,
          },
        }),
      title: locales.convertQuoteToInvoice,
    };

    const quoteMarkAsSentItem: MenuItem = {
      icon: 'send',
      onClick: () => openModal({ name: 'mark-as-sent', props: quote }),
      title: locales.markQuoteAsSent,
    };

    const quoteAcceptedItem: MenuItem = {
      icon: 'check',
      onClick: () =>
        openModal({
          name: 'mark-as-accepted',
          props: { ...quote, isFromList: true },
        }),
      title: locales.markQuoteAsAccepted,
    };

    const quoteArchivedItem: MenuItem = {
      icon: 'cross',
      onClick: () => openModal({ name: 'archive', props: quote }),
      title: locales.archiveQuote,
    };

    const quoteRevisedItem: MenuItem = {
      icon: 'edit',
      onClick: () => openModal({ name: 'revise', props: quote }),
      title: locales.reviseQuote,
    };

    const editItem: MenuItem = {
      icon: 'edit',
      onClick: () => history.push(routes.getEditQuote(quote.quoteId)),
      title: locales.edit,
    };

    const duplicateItem: MenuItem = {
      icon: 'copy',
      onClick: () => openModal({ name: 'duplicate', props: quote }),
      title: locales.duplicate,
    };

    const deleteItem: MenuItem = {
      colorScheme: 'red',
      icon: 'trash',
      onClick: () => openModal({ name: 'delete', props: quote }),
      title: locales.delete,
    };

    const resendItem: MenuItem = {
      icon: 'send',
      onClick: () =>
        history.push(`${routes.getQuote(quote.quoteId)}?resend=true`),
      title: locales.resend,
    };

    const menuItems = {
      ACCEPTED: [
        {
          ...(wasAlreadyConvertedToInvoice
            ? quoteSeeConvertedInvoiceItem
            : quoteTurnIntoInvoiceItem),
          hasBottomSeparator: true,
        },
        resendItem,
        duplicateItem,
      ],
      ARCHIVED: [duplicateItem],
      OVERDUE: [
        quoteAcceptedItem,
        quoteArchivedItem,
        { ...quoteRevisedItem, hasBottomSeparator: true },
        resendItem,
        duplicateItem,
      ],
      PENDING: [
        quoteMarkAsSentItem,
        { ...editItem, hasBottomSeparator: true },
        duplicateItem,
        deleteItem,
      ],
      SENT: [
        quoteAcceptedItem,
        quoteArchivedItem,
        { ...quoteRevisedItem, hasBottomSeparator: true },
        resendItem,
        duplicateItem,
      ],
    };

    return menuItems[status];
  }, [history, openModal, quote]);

  return <WorkListItem item={quote} menuItems={menuItemsByStatus} />;
};

export default QuoteListItem;
